/**
 * Profile API - reads and updates the authenticated user's own profile.
 * The `/api/user/profile` endpoint is only available to regular users; for
 * admin/operator tokens the caller uses the admin self-profile endpoints.
 */

import apiClient from '../../../api/client';
import { API_ENDPOINTS, getFullUrl } from '../../../constants/apiEndpoints';

export const getProfile = async (options) => {
    const response = await apiClient.get(API_ENDPOINTS.USER.PROFILE, {}, options);
    return response?.user ?? response ?? null;
};

export const updateProfile = async (data, options) => {
    const response = await apiClient.patch(API_ENDPOINTS.USER.PROFILE, data, {}, options);
    return response?.user ?? response ?? null;
};

export const getAdminProfile = async (options) => {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN_AUTH.ME, {}, options);
    return response?.admin ?? response ?? null;
};

export const updateAdminProfile = async (data, options) => {
    const response = await apiClient.patch(API_ENDPOINTS.ADMIN_AUTH.ME, data, {}, options);
    return response?.admin ?? response ?? null;
};

export const getStaffProfile = async (options) => {
    const response = await apiClient.get(API_ENDPOINTS.STAFF_AUTH.ME, {}, options);
    return response?.admin ?? response ?? null;
};

export const updateStaffProfile = async (data, options) => {
    const response = await apiClient.patch(API_ENDPOINTS.STAFF_AUTH.ME, data, {}, options);
    return response?.admin ?? response ?? null;
};

/**
 * Upload a new avatar for the authenticated regular user (multipart).
 * Returns the updated user object.
 */
export const uploadAvatar = async (file, options) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await apiClient.upload(API_ENDPOINTS.USER.AVATAR, formData, {}, options?.onUploadProgress);
    return response?.user ?? response ?? null;
};

/**
 * Upload a new avatar for the authenticated admin/operator (multipart).
 * Returns the updated admin object.
 */
export const uploadAdminAvatar = async (file, options) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await apiClient.upload(API_ENDPOINTS.ADMIN_AUTH.ME_AVATAR, formData, {}, options?.onUploadProgress);
    return response?.admin ?? response ?? null;
};

export const uploadStaffAvatar = async (file, options) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await apiClient.upload(API_ENDPOINTS.STAFF_AUTH.ME_AVATAR, formData, {}, options?.onUploadProgress);
    return response?.admin ?? response ?? null;
};

/**
 * Build an absolute URL for an uploaded avatar path (e.g. /uploads/avatars/...).
 */
export const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return '';
    if (/^https?:\/\//.test(avatarPath)) return avatarPath;
    return getFullUrl(avatarPath);
};

export default {
    getProfile,
    updateProfile,
    getAdminProfile,
    updateAdminProfile,
    uploadAvatar,
    uploadAdminAvatar,
    getAvatarUrl,
};